import AccountModel from 'tio-common/models/account';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import type MatchPaymentModel from './match-payment.ts';
import type FinancialInstitutionModel from './financial-institution.ts';
import type MatchAssetModel from './match-asset.ts';
import type PersonModel from './person.ts';
import type ObservableDocumentModel from './observable-document.ts';
import { Type } from '@warp-drive/core-types/symbols';

const _TRANSACTION_TYPE_VALUES = {
  PAYMENT: 'TransactionType.PAYMENT',
  INTEREST: 'TransactionType.INTEREST',
  AMBIGUOUS: 'TransactionType.AMBIGUOUS',
} as const;

export type AccountTransactionType =
  (typeof _TRANSACTION_TYPE_VALUES)[keyof typeof _TRANSACTION_TYPE_VALUES];

const _TRANSACTION_CATEGORY_VALUES = {
  DEBIT: 'TransactionCategory.DEBIT',
  CREDIT: 'TransactionCategory.CREDIT',
  AMBIGUOUS: 'TransactionCategory.AMBIGUOUS',
} as const;

export type AccountTransactionCategory =
  (typeof _TRANSACTION_CATEGORY_VALUES)[keyof typeof _TRANSACTION_CATEGORY_VALUES];

export const ACCOUNT_TRANSACTION_VERIFICATION_STATE = {
  PENDING_VERIFICATION: 'TransactionVerificationState.PENDING_VERIFICATION',
  VERIFIED: 'TransactionVerificationState.VERIFIED',
  REJECTED: 'TransactionVerificationState.REJECTED',
};

export type AccountTransactionVerificationState =
  (typeof ACCOUNT_TRANSACTION_VERIFICATION_STATE)[keyof typeof ACCOUNT_TRANSACTION_VERIFICATION_STATE];

type TransitionLogItem = {
  to: string;
  event: string;
  reason: {
    note: string;
  };
  timestamp: string;
};

export default class AccountTransactionModel extends Model {
  @attr declare amount: number;
  @attr declare transactionDate: string;
  @attr declare transactionType: AccountTransactionType;
  @attr declare category: AccountTransactionCategory;
  @attr declare verificationState: AccountTransactionVerificationState;
  @attr declare createdAt: string;
  @attr declare deletedAt: string | null;
  @attr declare transitionLog: TransitionLogItem[];

  @hasMany('match-payment', { async: false, inverse: null })
  declare matchPayments: MatchPaymentModel[];
  @hasMany('match-asset', { async: false, inverse: 'accountTransactions' })
  declare matchAssets: MatchAssetModel[];

  @belongsTo('financial-institution', { async: false, inverse: null })
  declare financialInstitution: FinancialInstitutionModel;
  @belongsTo('person', { async: false, inverse: 'accountTransactions' })
  declare person: PersonModel;
  @belongsTo('observable-document', { async: false, inverse: 'accountTransactions' })
  declare observableDocument: ObservableDocumentModel;
  @belongsTo('account', { async: false, inverse: 'accountTransactions' })
  declare account: AccountModel;

  get rejectionReason(): string {
    const { transitionLog = [] } = this;
    const rejection = transitionLog.find(
      (logItem: TransitionLogItem) => logItem.to === 'TransactionVerificationState.REJECTED'
    );
    return rejection?.reason?.note || '';
  }

  [Type] = 'account-transaction' as const;
}
